import React from 'react';
import { IconApiTestingOutline, IconPlusOutline } from '@saucelabs/ds-icons';
import { Box, Col, Flex, Row } from '@saucelabs/sl-component-grid';
import { Button } from '@saucelabs/ds-button';
import { LabelPill } from '@saucelabs/sl-component-label';
import { IconBulletChecked } from '@saucelabs/sl-component-icons';

import {
  ALLOWED_PROJECTS_VALUES,
  PRODUCT_API_TESTING,
  SALES_CONTACT_FORM,
  INTERVAL_KEY,
  PROJECTS_KEY,
} from '../../../constants';
import { Product } from '../Product';
import { SectionTitle } from '../../Layout/SectionTitle';
import { FeatureList } from '../../Plans/FeatureList';
import { PriceColumn } from '../PriceColumn';
import { PlanConfiguration } from '../../../records/PlanConfiguration';
import { getIntervalUnit } from '../../../utils';

interface ApiTestingTileProps {
  apiTestingConfig: PlanConfiguration,
  apiTestingPlans: Record<string, Plan>,
  hasApiTestingInCart: boolean,
  hasApiTestingSubscription: boolean,
  onApiTestingToggle: void,
  onSliderValueChange: (number, string, string) => void,
  price: number,
  subscriptionValues: Record<string, Plan>,
  updateProductConfig: (object) => void,
}
const ApiTestingTile: React.FC<ApiTestingTileProps> = ({
  apiTestingConfig,
  apiTestingPlans,
  hasApiTestingInCart,
  hasApiTestingSubscription,
  onApiTestingToggle,
  onSliderValueChange,
  price,
  subscriptionValues,
  updateProductConfig,
}) => (
  <Box.Section
    className="text-high-emphasis"
    data-qa={`product-section-${PRODUCT_API_TESTING}`}
  >
    <Row>
      <Col xs>
        <Flex
          alignItems="center"
          justifyContent="space-between"
        >
          <SectionTitle>
            <IconApiTestingOutline className="h-3 fill-current text-gray-500" />
            API Testing
            <LabelPill variant="light-gray">New</LabelPill>
          </SectionTitle>
        </Flex>
      </Col>
    </Row>
    <Row className="mt-1">
      <Col xs={8}>
        <p className="m-0 text-base text-gray-600">
          Minimize web services and API incidents in devops and CI/CD workflows with smarter
          feedback, simpler management and faster collaboration.
        </p>
      </Col>
    </Row>
    <Row between="xs">
      <Col xs>
        {(hasApiTestingInCart || hasApiTestingSubscription) ? (
          <Box.InnerBox
            className="mt-2 pb-2"
            data-qa={`product-box-${PRODUCT_API_TESTING}`}
          >
            <Product.Header>
              <Product.Name>Projects</Product.Name>
              <Product.IntervalChoice
                id={PRODUCT_API_TESTING}
                onChange={(isMonthly) => {
                  updateProductConfig({
                    [INTERVAL_KEY]: getIntervalUnit(isMonthly),
                    product: PRODUCT_API_TESTING,
                  });
                }}
                saving="20%"
                selectedInterval={apiTestingConfig.interval}
              />
            </Product.Header>
            <Product.Slogan>Projects contain API tests and results.</Product.Slogan>
            <Product.Description>
              <div className="flex flex-row justify-between">
                <Product.ApiTestingProjects
                  allowedValues={ALLOWED_PROJECTS_VALUES}
                  currentValue={apiTestingConfig.projects}
                  plan={apiTestingPlans[apiTestingConfig.interval]}
                  subscriptionValue={subscriptionValues[PRODUCT_API_TESTING]
                    ?.[PROJECTS_KEY]}
                  updateProjects={(projects) => onSliderValueChange(
                    projects,
                    PRODUCT_API_TESTING,
                    PROJECTS_KEY,
                  )}
                />
                <Product.MonthlyRate
                  rate={apiTestingPlans[apiTestingConfig.interval]
                    ?.calculateMonthlyRate(apiTestingConfig.projects)}
                />
              </div>
            </Product.Description>
          </Box.InnerBox>
        ) : (
          <>
            <div className="mt-2 mb-1"><strong>Includes:</strong></div>
            <FeatureList>
              <li>
                <IconBulletChecked />
                Functional Testing
              </li>
              <li>
                <IconBulletChecked />
                API Monitoring
              </li>
              <li>
                <IconBulletChecked />
                Integrate with CI/CD
              </li>
              <li>
                <IconBulletChecked />
                Sauce Connect Proxy
              </li>
            </FeatureList>
          </>
        )}
      </Col>
      {!(hasApiTestingInCart || hasApiTestingSubscription) && (
        <PriceColumn
          hasStartingPrice
          price={price}
        >
          <Button
            color="primary"
            elementType="button"
            onClick={onApiTestingToggle}
            prefixIcon={<IconPlusOutline />}
            size="md"
            type="button"
            variant="outline"
          >
            Add Plan
          </Button>
        </PriceColumn>
      )}
    </Row>
    {(hasApiTestingInCart || hasApiTestingSubscription) && (
      <Row
        between="xs"
        className="leading-7"
      >
        <Col xs>
          <span>
            Need more?
            {' '}
          </span>
          <a
            className="inline-block mt-2 cursor-pointer text-blue-700"
            data-qa="api-testing-sales"
            href={SALES_CONTACT_FORM}
            rel="noopener noreferrer"
            tabIndex={0}
            target="_blank"
          >
            Contact Sales
          </a>
        </Col>
        <Col
          className="text-right"
          xs
        >
          {!hasApiTestingSubscription && hasApiTestingInCart && (
            <Button
              className="mt-2"
              color="secondary"
              onClick={onApiTestingToggle}
              size="md"
              variant="outline"
            >
              Remove Plan
            </Button>
          )}
        </Col>
      </Row>
    )}
  </Box.Section>
);

export { ApiTestingTile };
